import { useMemo } from 'react';

import { useDataLayerContext } from '../../../utilities/context/static/DataLayerContext';
import {
  setFullstoryPageProperties,
  trackFullstoryEvent,
} from '~/utilities/helpers/syncGlobalObject';

interface ITrackPageProps {
  event: string;
  category: string;
  label: string;
  /** Sends a Fullstory event instead of page properties, for virtual page views that are not a real page */
  fullstoryEventName?: string;
}

interface ITrackEventProps {
  event: string;
  category: string;
  action: string;
  label: string;
  value: string | number;
}

function useTrackPage(
  /* NOSONAR */ { action, debug }: { action: 'pageview'; debug: boolean } = {
    action: 'pageview',
    debug: false,
  }
) {
  const { pushToDataLayer } = useDataLayerContext();

  const trackPage = useMemo(
    () => (props: ITrackPageProps) => {
      const { event, category, label, fullstoryEventName } = props;
      const eventsObject = {
        category,
        action,
        label,
      };

      const dataLayerObject = {
        events: eventsObject,
        event,
      };

      if (debug) {
        // eslint-disable-next-line no-console
        console.log('trackPageView :: dataLayerObject :: ', dataLayerObject);
      }

      pushToDataLayer(dataLayerObject);

      if (fullstoryEventName) {
        trackFullstoryEvent(fullstoryEventName, {
          campaign_category: category,
          campaign_path: label,
        });
      } else {
        setFullstoryPageProperties({ campaign_category: category, campaign_path: label });
      }
    },
    [action, debug, pushToDataLayer]
  );

  return trackPage;
}

function useTrackEvent(/* NOSONAR */ { debug } = { debug: false }) {
  const { pushToDataLayer } = useDataLayerContext();

  const trackEvent = useMemo(
    () => (props: ITrackEventProps) => {
      const { event, category, action, label, value } = props;
      const combinedLabel = `${category}_${action}_${label}_${value}`;
      const eventsObject = {
        category,
        action,
        label: combinedLabel,
      };

      const dataLayerObject = {
        events: eventsObject,
        event,
      };

      if (debug) {
        // eslint-disable-next-line no-console
        console.log('trackEvent :: dataLayerObject :: ', dataLayerObject);
      }

      pushToDataLayer(dataLayerObject);
      trackFullstoryEvent(combinedLabel, { category, label, value });
    },
    [debug, pushToDataLayer]
  );

  return trackEvent;
}

export { useTrackPage, useTrackEvent };
